import { useIntervalFn, useStorage, useUrlSearchParams } from '@vueuse/core'
import { compact } from 'lodash-es'
import { defineStore } from 'pinia'

import { clean, cloneDeep } from '@shared/object'

import { orderDrafts, orderGifts } from '@consumer/api'
import { decodeGift } from '@consumer/composables/giftTemplate'
import { useSharedData } from '@consumer/composables/sharedData'
import { defaultV3 } from '@consumer/logic/v3'
import type { GiftWizardProps } from '@consumer/modules/checkout/GiftWizardModal.vue'
import type { CheckoutProps } from '@consumer/pages/Checkout.vue'
import { trackAddToCart } from '@consumer/services/conversion'
import { purchaseOrder } from '@consumer/services/order/purchase'
import type { OrderGiftTemplate, OrderTemplate } from '@consumer/services/order/types'
import type {
  GiftsSentGift,
  GreetingCard,
  Order,
  OrderGift,
  OrderGiftGreeting,
  PurchasedOrder,
  PurchasedOrderGift,
} from '@consumer/types/serializers'
import { isPast } from '@consumer/utils/date'
import axios from '@shared/axios'
import { isEmail } from '@shared/regex'
import { openGiftWizard, openDeleteModal } from '@consumer/services/modals'
import { showNotice } from '@consumer/services/flash'
import { clearInternalOrderGiftTracking, getInternalOrderGiftTracking } from '@consumer/utils/internalOrderGiftTracking'
import { currentParams, replaceQuery } from '@shared/url'
import { uploadPhotoToServer, uploadVoiceMemoToServer } from '@consumer/modules/checkout/StepGreetingCard/V2/utils'
import type { GreetingCardWithCachedRiveFile } from '@consumer/modules/checkout/StepGreetingCard/V2/types'
import type { Attachment } from '@corp/serializers'
export type { Order, OrderGift, PurchasedOrder, PurchasedOrderGift }

export const LOCAL_STORAGE_KEY = 'giftly.stores.order'

const defaultOrderGiftDraft: OrderGiftTemplate = {
  amount: 100,
  secretSanta: false,
  giftlyMetadata: { designName: defaultV3.name },
  giftlyVersion: 3,
}

Object.freeze(defaultOrderGiftDraft)

type BuyAnotherProps = Omit<GiftWizardProps, 'orderGift'> & {
  orderGift: OrderGiftTemplate | PurchasedOrderGift | GiftsSentGift
}

export type OrderGiftUpdate = { order: Order; orderGift: OrderGift; feedItemId?: string }

type OrderWithRedirectUrl = OrderTemplate & { redirectUrl: string }

export function openBuyAnother ({ orderGift, ...props }: BuyAnotherProps) {
  return openGiftWizard({
    orderGift: {
      id: undefined,
      secretSanta: orderGift?.secretSanta || false,
      amount: orderGift.amount,
      giftlyMetadata: orderGift.giftlyMetadata,
      places: orderGift.places,
      giftlyVersion: orderGift.giftlyVersion,
      delivery: {
        deliveryMethod: orderGift.delivery?.deliveryMethod || 'email',
        recipientType: orderGift.delivery?.recipientType || null,
      },
      greeting: {
        card: orderGift.greeting?.card,
        categoryId: orderGift.greeting?.categoryId,
      },
    },
    specOptions: { spec: 'buy_again', purchaseMethod: 'buy-again' },
    ...props,
  })
}

function generateOrderGiftDraft () {
  return cloneDeep(defaultOrderGiftDraft)
}

export type ContactData = { gifterEmail?: string; gifterOptInMailer?: boolean }
export type SaveGiftData = Partial<OrderGiftTemplate> & ContactData

function orderGiftRequestData (order: Partial<OrderTemplate>, orderGift: SaveGiftData) {
  return {
    // Order params, ensures that we can auto-create the order if the current
    // one was purchased in a different tab or session.
    gifterEmail: order.gifterEmail,

    ...orderGift,

    placeIds: compact(orderGift.places?.map(place => place.giftlyId)),
  }
}

// Uploads greeting card customization photos to the server
// and deletes any unnecessary data from it.
async function sanitizeGreeting (greeting: OrderGiftGreeting) {
  let sanitizedGreeting = greeting

  const customizationStorySlides = await Promise.all(
    (sanitizedGreeting.greetingCardCustomization?.storySlides ?? []).map(
      async (slide) => {
        if (!slide.base64Photo) {
          return {
            ...slide,
            photo: typeof slide.photo === 'string' ? slide.photo : slide.photo?.signedId,
          }
        }

        const response = await uploadPhotoToServer(slide.base64Photo)

        return {
          ...slide,
          photo: response.signed_id,
          base64Photo: undefined, // Unset the base64Photo before saving to reduce payload size
        }
      },
    ),
  )

  // Upload voice memo if present as base64, or pass null to signal deletion.
  //
  // NOTE: Don't key the deletion branch off `base64VoiceMemo === null` — the
  // serializer always emits `base64_voice_memo: null` (it's a write-only field
  // on the model), so that signal would purge the memo on every edit. The
  // recorder's deleteRecording handler emits `voiceMemo: null`, so we use
  // that as the deletion signal instead.
  let voiceMemo = sanitizedGreeting.greetingCardCustomization?.voiceMemo
  const base64VoiceMemo = sanitizedGreeting.greetingCardCustomization?.base64VoiceMemo
  if (base64VoiceMemo) {
    const response = await uploadVoiceMemoToServer(base64VoiceMemo)
    voiceMemo = response.signed_id as unknown as typeof voiceMemo
  }
  else if (voiceMemo === null) {
    voiceMemo = null
  }
  else if (voiceMemo && typeof voiceMemo === 'object') {
    voiceMemo = (voiceMemo as Attachment).signedId as unknown as typeof voiceMemo
  }

  sanitizedGreeting = {
    ...greeting,
    greetingCardCustomization: {
      ...sanitizedGreeting.greetingCardCustomization,
      storySlides: customizationStorySlides,
      voiceMemo,
      base64VoiceMemo: undefined,
    },
  }

  // Remove the cached rive file to avoid JSON.stringify errors
  if (sanitizedGreeting.card && 'cachedRiveFile' in sanitizedGreeting.card)
    delete (sanitizedGreeting.card as GreetingCardWithCachedRiveFile).cachedRiveFile

  return sanitizedGreeting
}

export const useOrderStore = defineStore('order', () => {
  const { currentUser } = $(useSharedData())

  let contactEmail = $(useStorage('contactEmail', currentUser?.email))

  let order = $ref<OrderTemplate>(generateOrderDraft())
  let lastPurchasedOrder = $shallowRef<OrderWithRedirectUrl>()
  let lastAddedGift = $shallowRef<OrderGift>()

  // NOTE: The last custom greeting card the user uploaded, that way we can
  // allow them to re-select it if they chose a different card for another gift.
  const lastCustomGreetingCard = $ref<GreetingCard>()

  const orderGiftDraft = $computed(() => {
    return order.orderGiftDraft ||= generateOrderGiftDraft()
  })

  const completedCart = $computed(() => order.orderGifts.length > 0)
  const completedContact = $computed(() => isEmail(order.gifterEmail))
  let completedPayment = $ref(false)

  const readyToCheckout = computed(() => completedCart && completedContact && completedPayment)

  let store: ReturnType<typeof useOrderStore> | undefined

  const refreshStore = useIntervalFn(() => {
    const storeJSON = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (storeJSON) {
      const { order: persistedOrder, lastPurchasedOrder: purchasedOrder } = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY) || '{}',
      ) as { order: OrderTemplate; lastPurchasedOrder?: OrderWithRedirectUrl }

      // If another tab completed a purchase, reset the order state.
      if (purchasedOrder && purchasedOrder.id === order.id) {
        lastPurchasedOrder = purchasedOrder // Ensure we preserve it when $persist is called.
        $reset()
        // Redirect to the order confirmation URL if this tab was in checkout.
        if (location.pathname.includes('checkout'))
          location.href = purchasedOrder.redirectUrl
        return
      }

      if (
        persistedOrder
        && persistedOrder.updatedAt
        && order.updatedAt
        && persistedOrder.updatedAt > order.updatedAt
      ) {
        store ||= useOrderStore()
        store?.$hydrate()
      }
    }
  }, 1000)

  function resetIfPastDelivery (orderGift: OrderGift | OrderGiftTemplate) {
    if (isPast(orderGift.delivery?.deliveryDate)) {
      delete orderGift.delivery?.deliveryDate
      delete orderGift.delivery?.deliveryTime
    }
  }

  async function initialize ({ order: serverOrder, ...props }: CheckoutProps = {}) {
    // NOTE: The state in the server takes priority over local storage, unless
    // it's a stale prop from a back navigation.
    if (serverOrder && (!order || !order.updatedAt || serverOrder.updatedAt > order.updatedAt)) {
      order = serverOrder as any
    }
    else if (serverOrder === null && order.id) {
      // The current order was not found in the server, but it could be due to
      // the session expiring, so we should check if the order is still in pre-purchase.
      try {
        order = await orderDrafts.show(order)
      }
      catch {
        // The order in the store was probably purchased, so we should clear ids.
        resetOrderIds()
        order.orderGifts = [] // Clear cart.
      }
    }

    resetIfPastDelivery(orderGiftDraft)
    order.orderGifts.forEach(resetIfPastDelivery)

    if (currentUser) order.gifterEmail = currentUser.email // Ensure user email is set.
    order.gifterEmail ||= contactEmail // Persisted across purchases when no current user.
    contactEmail = order.gifterEmail

    const urlParams = useUrlSearchParams('history')

    if (location.pathname === '/gift-card-checkout') {
      if (urlParams.gift) {
        const gift = decodeGift(urlParams.gift.toString())
        Object.assign(orderGiftDraft, clean({
          amount: gift.amount,
          giftlyMetadata: gift.giftlyMetadata,
          giftlyVersion: gift.giftlyVersion,
        }))
      }
      else {
        Object.assign(orderGiftDraft, { ...props.customizerOptions })
        Object.assign(orderGiftDraft, clean({ purchaseMethod: props.purchaseMethod }))
      }
    }

    if (urlParams['gift-wizard']) {
      openGiftWizard({ orderGift: orderGiftDraft, origin: urlParams['gift-wizard'] as any })
    }
  }

  function generateOrderDraft (): OrderTemplate {
    return {
      orderGifts: [],
      orderGiftDraft: generateOrderGiftDraft(),
      gifterEmail: contactEmail,
      gifterOptInMailer: true,
    }
  }

  async function addToCart (orderGift: OrderGiftTemplate, options?: { skipToast?: boolean; storeInQuery?: boolean }) {
    // NOTE: In cases where the user has modified the same draft in different
    // tabs, they could end up with different designs or greeting cards in the
    // wizard, and they would be suprised if the gift added to the cart is not
    // the one they are looking at.
    await saveOrderGift(orderGift, undefined, { storeInQuery: options?.storeInQuery })

    const params = { id: orderGift.id, consumerOrderId: order.id }
    const data = getInternalOrderGiftTracking() ?? undefined
    const result = await orderGifts.addToCart<OrderGiftUpdate>({ params, data })
    clearInternalOrderGiftTracking()
    order = result.order
    Object.assign(orderGift, result.orderGift) // NOTE: Update the wizard state.

    const place = orderGift.places?.[0]
    if (place) trackAddToCart(result.feedItemId, place.name ?? '', orderGift.amount)

    if (!options?.skipToast) {
      lastAddedGift = order.orderGifts[0]
    }
  }

  async function removeOrderGift (
    orderGift: OrderGift | OrderGiftTemplate,
    options?: { onSuccess?: () => void; skipConfirmation?: boolean; skipNotification?: boolean },
  ) {
    const performDeletion = async () => {
      order = await orderGifts.destroy<Order>(orderGift)

      if (!options?.skipNotification) {
        showNotice('The gift has been removed from your cart.')
      }

      options?.onSuccess?.()
    }

    // If skipConfirmation is true, delete directly without modal
    if (options?.skipConfirmation) {
      return performDeletion()
    }

    // Otherwise, show confirmation modal
    return openDeleteModal({
      content: 'Do you really want to delete this gift from your cart?\n\nThis action is not reversible.',
      onConfirm: performDeletion,
    })
  }

  async function saveOrder (data: Partial<OrderTemplate>) {
    order = await orderDrafts.update<Order>({ params: order, data })
  }

  // Pre-set the buyer email when we already know it (e.g. a reminder link's
  // signed gifter email), mirroring the currentUser auto-fill in `initialize`.
  // Pre-completes the Contact step so the wizard opens on Delivery; the email
  // rides along with the next order-gift save (see orderGiftRequestData).
  function prefillGifterEmail (email: string) {
    order.gifterEmail ||= email
  }

  async function saveOrderGift (
    orderGift: OrderGiftTemplate,
    changes?: SaveGiftData,
    { storeInQuery }: { storeInQuery?: boolean } = { storeInQuery: false },
  ) {
    orderGift.id = orderGift.id || currentParams().get('orderGiftId') || undefined
    orderGift.consumerOrderId = orderGift.consumerOrderId || currentParams().get('orderId') || undefined

    if (changes?.greeting) {
      changes.greeting = await sanitizeGreeting(changes.greeting)
    }

    const save = orderGifts[orderGift.consumerOrderId ? 'update' : 'saveDraft']
    const data = cloneDeep(orderGiftRequestData(order, { ...orderGift, ...changes }))

    // Swap the photo by the signed id so that the server can attach it correctly
    // when deleting and recreating the story slides.
    if (
      data.greeting
        && !changes?.greeting
        && data.greeting.greetingCardCustomization
    ) {
      data.greeting.greetingCardCustomization.storySlides = data.greeting.greetingCardCustomization?.storySlides?.map(
        (slide) => {
          slide.photo = typeof slide.photo !== 'string' && slide.photo
            ? (slide.photo as Attachment).signedId
            : slide.photo
          return slide
        },
      )
    }

    const result = await save<OrderGiftUpdate>({ data })
    order = result.order

    // Preserve the order gift id in the url in case the user presses the back button.
    if (storeInQuery)
      replaceQuery({ orderGiftId: result.orderGift.id, orderId: result.order.id })

    Object.assign(orderGift, result.orderGift) // NOTE: Update the wizard state.

    // Reset the giftly cached image so that next time the rive preview image loads up it refetches the giftly image
    orderGift.giftlyImageBase64 = undefined
  }

  async function refreshOrderGiftDraft () {
    // Do not attempt to refresh it unless it was previously saved.
    if (!order.orderGiftDraft?.id) return null

    const result = await orderGifts.show<OrderGiftUpdate>({ params: order.orderGiftDraft })

    Object.assign(order.orderGiftDraft, result.orderGift)
  }

  async function placeOrder (attrs: { recaptchaToken?: string }) {
    const fees = order.fees! // Used to verify that the user is seeing the actual fees.
    const { redirectUrl } = await purchaseOrder({ fees, order, ...attrs })

    // NOTE: Avoid refreshing when other tabs reset the order state.
    refreshStore.pause()

    // TODO(cart): $reset in the confirmation page instead, to avoid clearing
    // the forms before navigation.

    // NOTE: Ensure we reset 'order' and let other tabs know that this is purchased.
    lastPurchasedOrder = { ...order, redirectUrl }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ lastPurchasedOrder }))

    // TODO(cart): Find the way to load facebook scripts from the
    // confirmation page to avoid a full page reload.
    window.location.href = redirectUrl
    return new Promise(() => {}) // Leave the page loading indicators running.
  }

  async function verifyBuyer () {
    if (order?.id) {
      try {
        const result = await orderDrafts.verifyGifter({ params: order })
        return result.passesVerification
      }
      catch {
        // we don't want a backend error during this verification to prevent a user
        // from proceeding with their purchase
        return true
      }
    }
    else {
      return true // in tests it happens sometimes that the order doesn't have an ID
    }
  }

  async function verifyBuyerViaSms () {
    const result = await orderDrafts.verifyGifterBySms({
      params: order,
      data: { phoneNumber: order.phoneNumber },
    })
    return result.smsSent
  }

  function resetOrderGiftIds (orderGift: OrderGiftTemplate) {
    delete orderGift.id
    delete orderGift.consumerOrderId
  }

  function resetOrderIds () {
    delete order.id
    order.orderGifts.forEach(resetOrderGiftIds)
    resetOrderGiftIds(orderGiftDraft)
    completedPayment = false
  }

  function $reset () {
    order = generateOrderDraft()
    completedPayment = false
  }

  function orderGiftFromParams () {
    const orderGiftId = currentParams().get('orderGiftId')

    if (!orderGiftId) return null

    return order.orderGifts.find(gift => gift.id === orderGiftId)
  }

  // Configure axios to send the current order in a header.
  //
  // This ensures that if the session was cleared but local storage is still
  // referencing a valid order, the server is able to find it and serve it back.
  axios.interceptors.request.use((request) => {
    request.headers['X-CONSUMER-ORDER-UUID'] = order.id
    return request
  })

  return {
    order: $$(order),
    orderGiftDraft: toRef(() => orderGiftDraft),
    readyToCheckout,
    initialize,
    saveOrder,
    prefillGifterEmail,
    addToCart,
    saveOrderGift,
    removeOrderGift,
    completedContact: $$(completedContact),
    completedCart: $$(completedCart),
    completedPayment: $$(completedPayment),
    count: computed(() => order.orderGifts.length),
    isEmpty: computed(() => !order.orderGifts.length),
    recentlyAddedGifts: computed(() => order.orderGifts.slice(0, 3)),
    otherGifts: computed(() => order.orderGifts.slice(3)),
    lastAddedGift: $$(lastAddedGift),
    lastCustomGreetingCard: $$(lastCustomGreetingCard),
    placeOrder,
    orderGiftFromParams,
    refreshOrderGiftDraft,
    verifyBuyer,
    verifyBuyerViaSms,
  }
}, {
  persist: {
    pick: ['order', 'lastAddedGift', 'lastPurchasedOrder', 'lastCustomGreetingCard'],
    key: LOCAL_STORAGE_KEY,
  },
})

export { useOrderStore as useCart }
